<template>
  <div>
    <div v-if="deployPermission" class="deploy-action">
      <a-button v-show="false" class="deploy-action_button" type="primary" :loading="loading" @click="onDeploy"> 一键部署 </a-button>
      <span v-show="false" class="deploy-action_count">{{ `已选择: ${selectedRowKeys.length} / ${dataSource.length}` }}</span>
    </div>
    <a-table ref="table" size="middle" row-key="release_service_id" :columns="columns" :data-source="dataSource" :pagination="false">
      <span slot="expandedRowRender" slot-scope="record">
        <a-descriptions v-if="[1, 2].includes(record.service_type.value)" size="small" :column="1">
          <a-descriptions-item label="GitTag">{{ record.git_tag_name }} </a-descriptions-item>
          <a-descriptions-item label="制品地址">{{ record.image_url }} </a-descriptions-item>
          <a-descriptions-item label="关联信息">
            <span v-if="!record.issues">--</span>
            <div v-for="issue in record.issues" :key="issue.key">
              <a class="custom-link" :href="issueLink(issue)" target="_blank">{{ issue.key }} {{ issue.title }} </a>
            </div>
          </a-descriptions-item>
          <a-descriptions-item v-if="Array.isArray(record.data_change) && record.data_change.length > 0" label="数据变更">
            <a-tag v-for="key in record.data_change" :key="key" color="pink">{{ dataChangeOptionMap[key].label }}</a-tag>
          </a-descriptions-item>
          <a-descriptions-item v-if="Array.isArray(record.configs) && record.configs.length > 0" label="确认配置项">
            <a-tag v-for="(item, index) in record.configs" :key="index" color="orange">{{ item }}</a-tag>
          </a-descriptions-item>
        </a-descriptions>
        <service-deploy-record :data-source="record.records" />
      </span>
      <span slot="serviceType" slot-scope="text, record"> {{ serviceTypes[record.service_type.value].label }}</span>
      <span slot="artifactVersion" slot-scope="text, record">
        <a-tooltip v-if="[1, 2].includes(record.service_type.value)">
          <template slot="title"> {{ record.image_url }} </template>
          <span class="artifact-ersion_value" @click="onVersionDetail(record.artifact_version_id)">{{ record.artifact_version }}</span>
        </a-tooltip>
        <span v-else-if="record.artifact_version_id !== null">
          {{ record.build_version }}
          <a-icon type="qrcode" @click="onQrCode(record)" />
        </span>
        <span v-else>{{ record.image }}</span>
      </span>
      <span slot="developers" slot-scope="text, record">
        <span v-for="developer in record.developers" :key="developer.id">
          <a-avatar size="small" :src="developer.avatar_url" /> <span class="creator-nickname">{{ developer.nickname }}; </span>
        </span>
      </span>
      <span slot="strategy" slot-scope="text, record">
        <a-popover v-if="record.strategy !== null">
          <template slot="content">
            <template v-for="(item, key) in record.strategy">
              <a-tag v-if="serviceStrategys[item]" :key="key" :color="serviceStrategys[item].color">{{ serviceStrategys[item].name }}</a-tag>
            </template>
          </template>
          <span>
            <template v-for="(item, key) in record.strategy">
              <a-tag v-if="serviceStrategys[item]" :key="key" :color="serviceStrategys[item].color">{{ serviceStrategys[item].name }}</a-tag>
            </template>
          </span>
        </a-popover>
      </span>
      <span slot="status" slot-scope="text, record">
        <a-badge :status="releaseDeployStatus[record.status].status" :text="releaseDeployStatus[record.status].label" />
      </span>
    </a-table>
    <qr-code-modal :is-show="qrCodeDisplay" :content="qrCodeData" @close="close" />
    <a-modal v-model="visible" title="选择部署环境" @ok="onSubmit">
      <a-form-model ref="form" :model="form" :rules="rules" :label-col="labelCol" :wrapper-col="wrapperCol">
        <a-form-model-item label="部署环境" prop="environment">
          <a-radio-group v-model="form.environment">
            <a-radio :value="2">QA环境</a-radio>
            <a-radio :value="6">Alpha环境</a-radio>
          </a-radio-group>
        </a-form-model-item>
      </a-form-model>
    </a-modal>
    <artifact-version-drawer ref="artifactVersionDrawerRef" />
  </div>
</template>

<script>
import { tagList } from '@/api/system/tag';
import { workflowReleaseDeploy } from '@/api/project/workflow';
import { releaseDeployStatus, serviceTypes, dataChangeOptions } from '@/utils/dictionary';

import QrCodeModal from '@/pages/tools/application/components/QrCodeModal';
import ArtifactVersionDrawer from '@/pages/project/service/components/ArtifactVersionDrawer';
import ServiceDeployRecord from '@/pages/project/workflow/release/components/ServiceDeployRecord';

const columns = [
  {
    title: 'ID',
    width: 80,
    align: 'center',
    dataIndex: 'release_service_id'
  },
  {
    title: '类型',
    dataIndex: 'type',
    width: 80,
    align: 'center',
    scopedSlots: { customRender: 'serviceType' }
  },
  {
    title: '服务',
    dataIndex: 'service_name'
  },
  {
    title: '制品版本',
    width: 180,
    dataIndex: 'artifact_version',
    scopedSlots: { customRender: 'artifactVersion' }
  },
  {
    title: '状态',
    width: 160,
    dataIndex: 'status',
    align: 'center',
    scopedSlots: { customRender: 'status' }
  },
  {
    title: '开发者',
    width: 160,
    dataIndex: 'developers',
    scopedSlots: { customRender: 'developers' }
  },
  {
    title: '发布策略',
    width: 120,
    ellipsis: true,
    dataIndex: 'strategy',
    scopedSlots: { customRender: 'strategy' }
  }
];
export default {
  components: { QrCodeModal, ServiceDeployRecord, ArtifactVersionDrawer },
  props: {
    dataSource: {
      type: Array,
      default: () => {
        return [];
      }
    },
    deployPermission: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      columns,
      visible: false,
      serviceStrategys: {},
      dataChangeOptions,
      qrCodeDisplay: false,
      qrCodeData: {},
      loading: false,
      form: {
        environment: 2
      },
      rules: {
        environment: [{ required: true, message: '发布环境不能为空', trigger: 'blur' }]
      },
      labelCol: { span: 6 },
      wrapperCol: { span: 16 },
      serviceTypes,
      releaseDeployStatus,
      releaseId: parseInt(this.$route.query.releaseId) || null,
      selectedRowKeys: []
    };
  },
  computed: {
    dataChangeOptionMap() {
      return this.dataChangeOptions.reduce((results, item, _) => {
        results[item.value] = item;
        return results;
      }, {});
    },
    issueLink() {
      return record => {
        if (record.issue_type === 'task') {
          const route = this.$router.resolve({
            name: 'page:project:task:detail',
            query: { taskId: record.id },
            params: { project_id: record.project_id }
          });
          return route.href;
        } else {
          const route = this.$router.resolve({
            name: 'page:project:quality:issue:detail',
            query: { issueId: record.id },
            params: { project_id: record.project_id }
          });
          return route.href;
        }
      };
    },
    rowSelection() {
      if (this.deployPermission) {
        return {
          onChange: selectedRowKeys => {
            this.selectedRowKeys = selectedRowKeys;
          },
          getCheckboxProps: record => ({
            props: {
              disabled: ![1, 2].includes(record.service_type.value) || [1, 2].includes(record.status), // 仅前端、后端可一键部署
              name: record.name
            }
          }),
          selectedRowKeys: this.selectedRowKeys
        };
      } else {
        return null;
      }
    }
  },
  mounted() {
    this.getTagData();
  },
  methods: {
    onQrCode(record) {
      this.qrCodeDisplay = true;
      this.qrCodeData = {
        app_name: '中免日上',
        download_url: record.download_url,
        platform: record.service_type.value === 3 ? 'iOS' : 'Android',
        category: record.category,
        build_version: record.build_version,
        created_time: record.created_time
      };
    },
    getTagData() {
      tagList({ group_code: 'release_strategy' }).then(res => {
        const tags = {};
        res.data.items.forEach(item => {
          tags[item.name] = item;
        });
        this.serviceStrategys = tags;
      });
    },

    close() {
      this.qrCodeDisplay = false;
      this.qrCodeData = {};
    },

    onSelectChange(selectedRowKeys) {
      this.selectedRowKeys = selectedRowKeys;
    },

    onDeploy() {
      if (this.selectedRowKeys.length === 0) {
        this.$message.warning('请选择需要部署的服务');
        return;
      }
      this.visible = true;
    },
    onSubmit() {
      this.loading = true;
      workflowReleaseDeploy({ environment: this.form.environment, release_services: this.selectedRowKeys })
        .then(() => {
          this.$message.success('部署任务提交成功，请耐心等待');
          this.loading = false;
          this.visible = false;
          this.selectedRowKeys = [];
          this.$emit('callback');
        })
        .catch(() => {
          this.loading = false;
        });
    },
    onVersionDetail(versionId) {
      this.$nextTick(() => {
        this.$refs.artifactVersionDrawerRef.onOpen(versionId);
      });
    }
  }
};
</script>

<style lang="less" scoped>
.deploy-action {
  margin-bottom: 16px;
  .deploy-action_button {
    margin-right: 16px;
  }
}
.artifact-ersion_value:hover {
  cursor: pointer;
}
</style>
